import { Injectable, NgZone, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';

import { PushNotifications, Token, PushNotificationSchema } from '@capacitor/push-notifications';
import { LocalNotifications, ActionPerformed } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';


@Injectable({
  providedIn: 'root'
})
export class VoipNotificationService {

  private readonly FCM_TOKEN_KEY = 'cx_fcm_token';
  public smsReceived = new EventEmitter<any>();
  public missedCallReceived = new EventEmitter<any>();

  constructor(
    private platform: Platform,
    private router: Router,
    private ngZone: NgZone
    // Inject your SipService here later
    // private sipService: SipService 
  ) { }

  public async initialize() {
    if (this.platform.is('android')) {
      await this.initializeAndroidPush();
    }
    
    // We will add iOS logic here later
    if (this.platform.is('ios')) {
      // await this.initializeIosPush();
    }
  }

  // --- Android Implementation ---

  private async initializeAndroidPush() {
    // 1. Create Channels
    await this.createAndroidChannels();

    // 2. Register for Push
    await PushNotifications.requestPermissions();
    await PushNotifications.register();

    // 3. Add Listeners
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('VOIPSERVICE: FCM TOKEN:', token.value);
      // TODO: Send this token to your backend server
      // this.sendTokenToServer(token.value);
      try {
        await this.storeFcmToken(token.value);
      } catch (error) {
        console.error('VOIPSERVICE: Failed to persist FCM token', error);
      }
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('VOIPSERVICE:FCM Error:', error);
    });

    // This is the main listener for when a push is received
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('VOIPSERVICE: Push notification received: ', notification);

      // Here we will check the "type" of notification
      if (notification.data?.type === 'incoming_call') {
        this.handleVoipPush(notification);
      } else if (notification.data?.type === 'incoming_sms') {
        this.handleMessagePush(notification);
      } else if (notification.data?.type === 'missed_call') {
        this.handleMissedCallPush(notification);
      } else {
        // Handle other types or a default case
        console.warn('Unknown push notification type');
      }
    });

    // This listener is for when a user TAPS a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('VOIPSERVICE: Push notification action performed', action);
      const data = action.notification?.data;
      if (data?.type === 'incoming_call') {
        const autoAnswer = action.actionId === 'answer';
        this.navigateToIncomingCall(data, { autoAnswer });
      } else if (data?.type === 'incoming_sms') {
        this.navigateToSendMessage(data);
      } else if (data?.type === 'missed_call') {
        this.navigateToCallDetail(data);
      }
    });

    // This listener is for when a user taps "Answer" or "Decline"
    LocalNotifications.addListener('localNotificationActionPerformed', (action: ActionPerformed) => {
      console.log('VOIPSERVICE: Local notification action performed', action);
      
      const notificationData = action.notification.extra;
      
      // Check notification type before routing
      if (notificationData?.type === 'incoming_call') {
        if (action.actionId === 'answer') {
          // Navigate to call-voip page with pickupCallId parameter
          console.log('VOIPSERVICE: Answer action - navigating to call-voip with callId:', notificationData.callId);
          this.navigateToIncomingCall(notificationData, { autoAnswer: true });
        } else if (action.actionId === 'decline') {
          // Dismiss the notification and optionally send decline to backend
          console.log('VOIPSERVICE: Decline action - dismissing call:', notificationData.callId);
          // TODO: Notify backend that call was declined
        } else if (action.actionId === 'tap') {
          // User tapped the notification body
          this.navigateToIncomingCall(notificationData);
        }
      } else if (notificationData?.type === 'incoming_sms') {
        if (action.actionId === 'tap') {
          this.navigateToSendMessage(notificationData);
        }
      } else if (notificationData?.type === 'missed_call') {
        // User tapped the missed call notification - navigate to call detail
        this.navigateToCallDetail(notificationData);
      }
    });
  }

  private navigateToIncomingCall(callData: any, options: { autoAnswer?: boolean } = {}) {
    this.ngZone.run(() => {
      const queryParams: Record<string, any> = {
        pickupCallId: callData.callId,
        callerName: callData.callerName,
        callerNumber: callData.callerNumber
      };

      if (options.autoAnswer) {
        queryParams.autoAnswer = 'true';
      }

      this.router.navigate(['/call-voip'], {
        queryParams
      });
    });
  }

  private async createAndroidChannels() {
    try {
      await LocalNotifications.createChannel({
        id: 'voip_channel',
        name: 'Incoming Calls',
        description: 'Notifications for incoming VoIP calls',
        importance: 5,
        sound: 'content://settings/system/ringtone', // System ringtone URI
        vibration: true,
      });

      await LocalNotifications.createChannel({
        id: 'messages_channel',
        name: 'General Messages',
        description: 'General notifications, new messages, etc.',
        importance: 3,
        vibration: true,
      });

      await LocalNotifications.createChannel({
        id: 'missed_calls_channel',
        name: 'Missed Calls',
        description: 'Notifications for missed calls',
        importance: 4,
        vibration: true,
      });

      console.log('VOIPSERVICE: Android Notification Channels created.');
    } catch (error) {
      console.error('VOIPSERVICE:Error creating channels:', error);
    }
  }

  private async handleVoipPush(notification: PushNotificationSchema) {
    console.log('VOIPSERVICE: Handling VoIP Push...', notification.data);
    
    const callData = notification.data;
    
    // Generate a valid Java int notification ID (max 2147483647)
    const notificationId = Math.floor(Math.random() * 2147483647);
    
    // Register the action buttons FIRST (must be done before scheduling)
    try {
      await LocalNotifications.registerActionTypes({
        types: [
          {
            id: 'VOIP_CALL',
            actions: [
              {
                id: 'answer',
                title: 'Answer',
              },
              {
                id: 'decline',
                title: 'Decline',
              }
            ]
          }
        ]
      });
      console.log('VOIPSERVICE: Action types registered');
    } catch (error) {
      console.log('VOIPSERVICE: Action types already registered or error:', error);
    }
    
    // Show local notification with Answer/Decline buttons
    await LocalNotifications.schedule({
      notifications: [
        {
          title: 'Incoming Call',
          body: `${callData.callerName || 'Unknown'} (${callData.callerNumber || 'Unknown'})`,
          id: notificationId,
          channelId: 'voip_channel',
          extra: callData,
          actionTypeId: 'VOIP_CALL',
          ongoing: true, // Makes it persistent
        }
      ]
    });
    
    console.log('VOIPSERVICE: Local notification scheduled with ID:', notificationId);
  }

  private async handleMessagePush(notification: PushNotificationSchema) {
    console.log('VOIPSERVICE: Handling Message Push...', notification.data);
    const data = notification.data;
    
    // Show local notification for foreground/background consistency if needed
    // Note: If the app is in background, the system notification from FCM (notification payload) usually shows up.
    // But if we want to ensure it shows or customize it, we can use LocalNotifications.
    // However, duplicate notifications might occur if FCM notification payload is present.
    // Since the user asked for "in any case", and FCM notification payload handles background/killed,
    // we mainly need to handle foreground here.
    
    // Generate a unique ID
    const notificationId = Math.floor(Math.random() * 2147483647);

    await LocalNotifications.schedule({
      notifications: [
        {
          title: notification.title || `New Message from ${data.contactNumber}`,
          body: notification.body || data.messageBody,
          id: notificationId,
          channelId: 'messages_channel',
          extra: data,
          smallIcon: 'ic_stat_icon_config_sample', // Ensure this resource exists or use default
        }
      ]
    });

    // Emit event so pages can refresh their lists
    this.smsReceived.emit(data);
  }

  private async handleMissedCallPush(notification: PushNotificationSchema) {
    console.log('VOIPSERVICE: Handling Missed Call Push...', notification.data);
    const data = notification.data;
    
    // Generate a unique ID
    const notificationId = Math.floor(Math.random() * 2147483647);

    await LocalNotifications.schedule({
      notifications: [
        {
          title: notification.title || 'Missed Call',
          body: notification.body || `${data.callerName} (${data.callerNumber})`,
          id: notificationId,
          channelId: 'missed_calls_channel',
          extra: data,
          smallIcon: 'ic_stat_icon_config_sample',
        }
      ]
    });

    // Emit event so pages can refresh their lists
    this.missedCallReceived.emit(data);
  }

  private navigateToCallDetail(data: any) {
    this.ngZone.run(() => {
      // Construct call information object matching the expected format for detail page
      const callInfo = {
        uuid: data.callId,
        fromNumber: data.callerNumber,
        fromName: data.callerName,
        toNumber: data.toNumber,
        toName: data.toName,
        direction: data.direction || 'in',
        answered: false,
        state: 'hangup',
        createdAtUnixTimestamp: data.createdAt ? parseInt(data.createdAt) : Math.floor(Date.now() / 1000),
        type: 'missedCall',
        contact: {
          name: {
            display: data.callerName || data.callerNumber,
            given: 'Unknown',
            family: 'Unknown'
          },
          phones: [
            {
              type: 'mobile',
              isPrimary: false,
              number: data.callerNumber
            }
          ]
        }
      };

      this.router.navigate(['/detail'], {
        state: {
          information: callInfo
        }
      });
    });
  }

  private navigateToSendMessage(data: any) {
    this.ngZone.run(() => {
      // Construct a minimal contact object
      const contact = {
        name: { display: data.contactNumber }, // We might not have the name resolved yet
        selected_contact_phone_number: data.contactNumber,
        phones: [{ number: data.contactNumber }]
      };

      // Construct a minimal profile object
      const profile = {
        number: data.profileNumber,
        id: data.profileId // If available, though push server didn't send it in my edit. I should check.
      };

      this.router.navigate(['/send-message'], {
        state: {
          selected_profile: profile,
          contact: contact // We might need to update SendMessagePage to look for this in state
        }
      });
    });
  }

  private async storeFcmToken(token: string): Promise<void> {
    await Preferences.set({ key: this.FCM_TOKEN_KEY, value: token });
  }

  public async getStoredFcmToken(): Promise<string | null> {
    const { value } = await Preferences.get({ key: this.FCM_TOKEN_KEY });
    return value ?? null;
  }
}